import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PlusIcon, ChevronIcon, TrashIcon, EditIcon } from "../icons";
import { fmtMonth } from "../utils/helpers";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const CATEGORIES = ["Filament", "Acrylic", "Hardware", "Machinery", "Parts", "Shipping Supplies", "Software", "Subscriptions", "Office Expenses", "Transportation", "Food"];
// Must match DEFAULT_PURCHASERS in ExpensesPage.js — reimbursement tracking
// keys off these exact strings.
const PURCHASERS = ["Daniel - Personal Card", "Jonathan - Personal Card", "Company Card", "Company Account", "Petty Cash"];

const fmtMoney = (v) => `$${parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIELD_LABELS = { name: "Name", category: "Category", paidBy: "Paid By", notes: "Notes", totalAmount: "Amount", months: "Months", purchaseMonth: "Purchase Month", startMonth: "First Payment" };
const fmtLogVal = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "totalAmount") return fmtMoney(v);
  if (field === "startMonth" || field === "purchaseMonth") return fmtMonth(v);
  return String(v);
};

// Due dates are always the 1st of the month FOLLOWING the purchase.
const nextMonthKey = (monthKey) => {
  if (!/^\d{4}-\d{2}$/.test(monthKey || "")) return "";
  let [y, m] = monthKey.split("-").map(Number);
  m += 1;
  if (m > 12) { m = 1; y += 1; }
  return `${y}-${String(m).padStart(2, "0")}`;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const prevMonthKey = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const EMPTY_FORM = { name: "", totalAmount: "", months: "3", purchaseMonth: "", category: CATEGORIES[0], paidBy: PURCHASERS[0], notes: "" };

export default function FinancingPage({ store, storeName }) {
  const [financings, setFinancings] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [collapsed, setCollapsed] = useState({});
  const [busy, setBusy] = useState({}); // `${id}-${idx}` → true while a send/undo is in flight
  const [payEarlyFor, setPayEarlyFor] = useState(null); // financing id showing the month choice
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState("");
  const [error, setError] = useState("");

  const currentUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("nycmfg-user")); } catch { return null; }
  }, []);

  const fetchFinancings = useCallback(() => {
    fetch(`${API_URL}/api/financings?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setFinancings(data); })
      .catch(() => {});
  }, [store]);

  useEffect(() => { fetchFinancings(); }, [fetchFinancings]);

  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const monthlyPreview = useMemo(() => {
    const total = parseFloat(form.totalAmount) || 0;
    const months = parseInt(form.months, 10) || 0;
    if (total <= 0 || months < 1) return null;
    return total / months;
  }, [form.totalAmount, form.months]);

  const saveNew = () => {
    setError("");
    const total = parseFloat(form.totalAmount) || 0;
    const months = parseInt(form.months, 10) || 0;
    if (!form.name.trim() || total <= 0 || months < 1 || !form.purchaseMonth) {
      setError("Name, amount, months, and purchase month are required.");
      return;
    }
    fetch(`${API_URL}/api/financings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        store, name: form.name.trim(), totalAmount: total, months,
        purchaseMonth: form.purchaseMonth, category: form.category, paidBy: form.paidBy,
        notes: form.notes, createdBy: currentUser?.name || "",
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setError(d.error); return; }
        setAdding(false);
        setForm(EMPTY_FORM);
        fetchFinancings();
      })
      .catch(() => setError("Could not save — is the server reachable?"));
  };

  const sendPayment = (fin, idx, undo) => {
    const key = `${fin.id}-${idx}`;
    if (busy[key]) return;
    setBusy((b) => ({ ...b, [key]: true }));
    fetch(`${API_URL}/api/financings/${fin.id}/${undo ? "unsend-payment" : "send-payment"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ index: idx }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.error) setFinancings((list) => list.map((f) => (f.id === fin.id ? d : f)));
      })
      .catch(() => {})
      .finally(() => setBusy((b) => ({ ...b, [key]: false })));
  };

  const payEarly = (fin, month) => {
    const key = `${fin.id}-early`;
    if (busy[key]) return;
    setBusy((b) => ({ ...b, [key]: true }));
    fetch(`${API_URL}/api/financings/${fin.id}/pay-early`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ month }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.error) setFinancings((list) => list.map((f) => (f.id === fin.id ? d : f)));
      })
      .catch(() => {})
      .finally(() => {
        setBusy((b) => ({ ...b, [key]: false }));
        setPayEarlyFor(null);
      });
  };

  const startEdit = (fin) => {
    setEditError("");
    setEditingId(fin.id);
    setEditForm({
      name: fin.name, category: fin.category || CATEGORIES[0], paidBy: fin.paidBy || PURCHASERS[0],
      notes: fin.notes || "", totalAmount: String(fin.totalAmount || ""), months: String(fin.months || ""),
      purchaseMonth: fin.purchaseMonth || "",
    });
  };

  const saveEdit = (fin) => {
    setEditError("");
    const body = {
      name: editForm.name, category: editForm.category, paidBy: editForm.paidBy,
      notes: editForm.notes, editedBy: currentUser?.name || "",
    };
    if ((fin.paymentsSent || 0) === 0) {
      body.totalAmount = parseFloat(editForm.totalAmount) || 0;
      body.months = parseInt(editForm.months, 10) || 0;
      if (editForm.purchaseMonth) body.purchaseMonth = editForm.purchaseMonth;
    }
    fetch(`${API_URL}/api/financings/${fin.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setEditError(d.error); return; }
        setFinancings((list) => list.map((f) => (f.id === fin.id ? d : f)));
        setEditingId(null);
      })
      .catch(() => setEditError("Could not save — is the server reachable?"));
  };

  const deleteFinancing = (fin) => {
    const sent = fin.paymentsSent || 0;
    const msg = sent > 0
      ? `Delete "${fin.name}"? The ${sent} payment${sent === 1 ? "" : "s"} already sent will STAY on the Expenses page — only this tracker is removed.`
      : `Delete "${fin.name}"?`;
    if (!window.confirm(msg)) return;
    fetch(`${API_URL}/api/financings/${fin.id}`, { method: "DELETE" })
      .then(() => fetchFinancings())
      .catch(() => {});
  };

  // Stats
  const totals = useMemo(() => {
    let financed = 0, sent = 0, remaining = 0, active = 0;
    financings.forEach((f) => {
      financed += f.totalAmount || 0;
      (f.payments || []).forEach((p) => {
        if (p.expenseId) sent += p.amount || 0;
        else remaining += p.amount || 0;
      });
      if ((f.paymentsSent || 0) < (f.payments || []).length) active += 1;
    });
    return { financed, sent, remaining, active };
  }, [financings]);

  const nowKey = currentMonthKey();

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Financing</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track financed purchases and send each installment to that month's expenses</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Active Financings</span><span style={{ ...S.statValue, color: "#0033FF" }}>{totals.active}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Financed</span><span style={{ ...S.statValue, color: "#0A0A0F" }}>{fmtMoney(totals.financed)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Sent to Expenses</span><span style={{ ...S.statValue, color: "#34d399" }}>{fmtMoney(totals.sent)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Still Owed</span><span style={{ ...S.statValue, color: "#ef4444" }}>{fmtMoney(totals.remaining)}</span></div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button onClick={() => { setAdding(true); setForm({ ...EMPTY_FORM, purchaseMonth: currentMonthKey() }); }} style={S.addBtn}><PlusIcon /> New Financing</button>
      </div>

      {/* Add form */}
      {adding && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px 16px" }}>
            <div>
              <label style={S.label}>Purchase Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Filament bulk order" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Order Amount ($)</label>
              <input type="number" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: e.target.value })} placeholder="3000.00" step="0.01" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Months</label>
              <input type="number" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} min="1" max="120" step="1" style={S.input} />
            </div>
            <div>
              <label style={S.label}>Purchase Month</label>
              <input type="month" value={form.purchaseMonth} onChange={(e) => setForm({ ...form, purchaseMonth: e.target.value })} style={{ ...S.input, colorScheme: "dark" }} />
            </div>
            <div>
              <label style={S.label}>Expense Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={S.input}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Paid By</label>
              <select value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} style={S.input}>
                {PURCHASERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={S.label}>Notes (optional)</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Vendor, terms, interest…" style={S.input} />
          </div>
          {monthlyPreview !== null && (
            <div style={{ marginTop: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0033FF", fontWeight: 700 }}>
              ≈ {fmtMoney(monthlyPreview)} / month × {parseInt(form.months, 10) || 0} months
              {form.purchaseMonth && <span style={{ color: "rgba(10,10,15,0.58)", fontWeight: 400 }}> — first payment due {fmtMonth(nextMonthKey(form.purchaseMonth))} 1st (month after purchase)</span>}
            </div>
          )}
          {error && <div style={{ marginTop: 10, color: "#ef4444", fontSize: 13, fontWeight: 600 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <button onClick={() => { setAdding(false); setError(""); }} style={S.clearBtn}>Cancel</button>
            <button onClick={saveNew} style={S.saveJobBtn}>Save</button>
          </div>
        </div>
      )}

      {/* Financing cards */}
      {financings.map((fin) => {
        const payments = fin.payments || [];
        const sentCount = fin.paymentsSent || 0;
        const done = sentCount >= payments.length && payments.length > 0;
        const isOpen = collapsed[fin.id] !== true;
        const sentAmt = payments.reduce((s, p) => s + (p.expenseId ? p.amount : 0), 0);

        return (
          <div key={fin.id} style={{ marginBottom: 12, opacity: done ? 0.75 : 1 }}>
            <button onClick={() => toggle(fin.id)} style={S.monthHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fin.name}</span>
                {fin.category && <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, padding: "3px 8px", border: "2px solid #0A0A0F", color: "#0A0A0F", flexShrink: 0 }}>{fin.category}</span>}
                {done && <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, padding: "3px 8px", background: "#34d399", color: "#0c3a2a", flexShrink: 0 }}>Paid Off</span>}
                {(fin.editLog || []).length > 0 && <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, padding: "3px 8px", background: "#f59e0b", color: "#0A0A0F", flexShrink: 0 }} title="This plan has been edited — see Edit History">Edited ×{fin.editLog.length}</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, flexShrink: 0 }}>
                <span style={{ color: "#0033FF", fontWeight: 700 }}>{fmtMoney(fin.totalAmount)}</span>
                <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13 }}>{sentCount}/{payments.length} sent</span>
              </div>
            </button>
            {isOpen && (
              <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderTop: "none", borderRadius: 0, padding: "16px 20px" }}>
                {/* Edit form */}
                {editingId === fin.id && (
                  <div style={{ border: "2px solid #f59e0b", padding: 14, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "#f59e0b", marginBottom: 10 }}>Editing — all changes are logged with your name</div>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "10px 16px" }}>
                      <div>
                        <label style={S.label}>Purchase Name</label>
                        <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Expense Category</label>
                        <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} style={S.input}>
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>Paid By</label>
                        <select value={editForm.paidBy} onChange={(e) => setEditForm({ ...editForm, paidBy: e.target.value })} style={S.input}>
                          {PURCHASERS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      {(fin.paymentsSent || 0) === 0 && (
                        <>
                          <div>
                            <label style={S.label}>Order Amount ($)</label>
                            <input type="number" value={editForm.totalAmount} onChange={(e) => setEditForm({ ...editForm, totalAmount: e.target.value })} step="0.01" style={S.input} />
                          </div>
                          <div>
                            <label style={S.label}>Months</label>
                            <input type="number" value={editForm.months} onChange={(e) => setEditForm({ ...editForm, months: e.target.value })} min="1" max="120" step="1" style={S.input} />
                          </div>
                          <div>
                            <label style={S.label}>Purchase Month</label>
                            <input type="month" value={editForm.purchaseMonth} onChange={(e) => setEditForm({ ...editForm, purchaseMonth: e.target.value })} style={{ ...S.input, colorScheme: "dark" }} />
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <label style={S.label}>Notes</label>
                      <input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} style={S.input} />
                    </div>
                    {(fin.paymentsSent || 0) > 0 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "rgba(10,10,15,0.58)" }}>Amount / months / start month are locked because payments were already sent — undo them first to reshape the schedule.</div>
                    )}
                    {editError && <div style={{ marginTop: 8, color: "#ef4444", fontSize: 13, fontWeight: 600 }}>{editError}</div>}
                    <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                      <button onClick={() => setEditingId(null)} style={S.clearBtn}>Cancel</button>
                      <button onClick={() => saveEdit(fin)} style={S.saveJobBtn}>Save Changes</button>
                    </div>
                  </div>
                )}
                {/* Progress bar */}
                <div style={{ display: "flex", height: 10, marginBottom: 14, border: "2px solid #0A0A0F" }}>
                  <div style={{ width: `${fin.totalAmount > 0 ? (sentAmt / fin.totalAmount) * 100 : 0}%`, background: "#34d399", transition: "width .3s" }} />
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                  {fin.purchaseMonth && <span>Purchased: <span style={{ color: "#0A0A0F", fontWeight: 700 }}>{fmtMonth(fin.purchaseMonth)}</span></span>}
                  <span>Monthly: <span style={{ color: "#0A0A0F", fontWeight: 700 }}>{fmtMoney(payments[0]?.amount)}</span></span>
                  <span>Sent: <span style={{ color: "#34d399", fontWeight: 700 }}>{fmtMoney(sentAmt)}</span></span>
                  <span>Remaining: <span style={{ color: "#ef4444", fontWeight: 700 }}>{fmtMoney((fin.totalAmount || 0) - sentAmt)}</span></span>
                  {fin.paidBy && <span>Paid by: <span style={{ color: "#0A0A0F", fontWeight: 700 }}>{fin.paidBy}</span></span>}
                  {fin.createdBy && <span>By: <span style={{ color: "#0A0A0F", fontWeight: 700 }}>{fin.createdBy}</span></span>}
                </div>
                {fin.notes && <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 14 }}>{fin.notes}</div>}

                {/* Schedule */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["#", "Month", "Amount", "Status", ""].map((h) => (
                        <th key={h} style={{ textAlign: h === "Amount" ? "right" : "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", padding: "6px 8px", borderBottom: "2px solid #0A0A0F" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, idx) => {
                      const isSent = !!p.expenseId;
                      const isDue = !isSent && p.month <= nowKey;
                      const isBulk = isSent && payments.filter((q) => q.expenseId === p.expenseId).length > 1;
                      const key = `${fin.id}-${idx}`;
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid rgba(10,10,15,0.15)" }}>
                          <td style={{ padding: "8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{idx + 1}/{payments.length}</td>
                          <td style={{ padding: "8px", fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{fmtMonth(p.month)}</td>
                          <td style={{ padding: "8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: "#0A0A0F" }}>{fmtMoney(p.amount)}</td>
                          <td style={{ padding: "8px" }}>
                            {isSent ? (
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#34d399", textTransform: "uppercase", letterSpacing: 0.5 }}>{isBulk ? "✓ Paid Early" : "✓ In Expenses"}{p.sentAt ? ` · ${p.sentAt.slice(0, 10)}` : ""}</span>
                            ) : (
                              <span style={{ fontSize: 12, fontWeight: 700, color: isDue ? "#ef4444" : "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 0.5 }}>{isDue ? "Due" : "Upcoming"}</span>
                            )}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            {isSent ? (
                              <button onClick={() => sendPayment(fin, idx, true)} disabled={busy[key]} title={isBulk ? "Undoes the whole early-payoff expense (all payments it covers)" : "Remove this expense"} style={{ ...S.clearBtn, padding: "5px 10px", fontSize: 12, opacity: busy[key] ? 0.5 : 1 }}>Undo</button>
                            ) : (
                              <button onClick={() => sendPayment(fin, idx, false)} disabled={busy[key]} style={{ ...S.saveJobBtn, padding: "5px 12px", fontSize: 12, opacity: busy[key] ? 0.5 : 1 }}>Send to Expenses</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Edit history — tamper trail */}
                {(fin.editLog || []).length > 0 && (
                  <div style={{ marginTop: 14, paddingTop: 10, borderTop: "2px solid #0A0A0F" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "#f59e0b", marginBottom: 6 }}>Edit History</div>
                    {fin.editLog.slice().reverse().map((entry, i) => (
                      <div key={i} style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: "rgba(10,10,15,0.58)", marginBottom: 4, lineHeight: 1.6 }}>
                        <span style={{ color: "#f59e0b", fontWeight: 700 }}>{(entry.at || "").slice(0, 16).replace("T", " ")}</span>
                        {" · "}
                        <span style={{ color: "#0A0A0F", fontWeight: 700 }}>{entry.by || "unknown"}</span>
                        {" — "}
                        {(entry.changes || []).map((c) => `${FIELD_LABELS[c.field] || c.field}: ${fmtLogVal(c.field, c.from)} → ${fmtLogVal(c.field, c.to)}`).join(" · ")}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {!done && payEarlyFor !== fin.id && (
                      <button onClick={() => setPayEarlyFor(fin.id)} style={{ ...S.addBtn, fontSize: 12, padding: "6px 12px" }}>
                        Pay Early — {fmtMoney((fin.totalAmount || 0) - sentAmt)}
                      </button>
                    )}
                    {!done && payEarlyFor === fin.id && (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(10,10,15,0.58)" }}>Post remaining {fmtMoney((fin.totalAmount || 0) - sentAmt)} as one expense in:</span>
                        <button onClick={() => payEarly(fin, currentMonthKey())} disabled={busy[`${fin.id}-early`]} style={{ ...S.saveJobBtn, padding: "5px 12px", fontSize: 12, opacity: busy[`${fin.id}-early`] ? 0.5 : 1 }}>{fmtMonth(currentMonthKey())}</button>
                        <button onClick={() => payEarly(fin, prevMonthKey())} disabled={busy[`${fin.id}-early`]} style={{ ...S.saveJobBtn, padding: "5px 12px", fontSize: 12, opacity: busy[`${fin.id}-early`] ? 0.5 : 1 }}>{fmtMonth(prevMonthKey())}</button>
                        <button onClick={() => setPayEarlyFor(null)} style={{ ...S.clearBtn, padding: "5px 10px", fontSize: 12 }}>Cancel</button>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {editingId !== fin.id && <button onClick={() => startEdit(fin)} style={S.iconBtn} title="Edit financing (logged)"><EditIcon /></button>}
                    <button onClick={() => deleteFinancing(fin)} style={{ ...S.iconBtn, borderColor: "#ef4444", color: "#ef4444" }} title="Delete financing"><TrashIcon /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {financings.length === 0 && !adding && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
          No financings yet. Click "New Financing" to split a purchase across monthly payments.
        </div>
      )}
    </div>
  );
}
